import type { TrainingScenarioImageTagView } from "@/api";

// Evaluation 이미지 태그를 3그룹으로 순위 매기는 순수 로직.
//   - 1그룹(Recommended): 엄격 semver(X.Y.Z) 태그이면서 metadata probe 가 성공한 태그. semver 내림차순.
//   - 2그룹: semver 코어 + dev 접미사 태그. 코어 내림차순, 그다음 dev 번호 내림차순.
//   - 3그룹: 나머지 전부(legacy 태그, probe 가 실패한 semver 태그 포함). 알파벳 오름차순.
// 목록 응답의 version 필드는 GAR digest 리소스명이라 판정에 쓸 수 없다. 태그 문자열에서 파싱한다.
// 백엔드 정렬은 역사전순이라 1.10.0 이 1.9.0 아래에 온다. 그래서 semver 그룹은 숫자 비교로 다시 정렬한다.

export type RankedImageTag = {
  tag: string;
  imageUri: string;
  recommended: boolean;
};

type SemverTriple = {
  major: number;
  minor: number;
  patch: number;
};

// dev 는 번호 없는 "dev" 접미사를 -1 로 두어 "1.2.3-dev0" 보다 아래에 정렬한다.
type DevSemver = SemverTriple & { dev: number };

// 앞의 v 를 허용하지 않는다. v1.2.3 과 1.2.3 이 중복 후보가 되지 않게 하고, legacy v-태그는 3그룹에 둔다.
const STRICT_SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)$/;
// dev 번호 뒤 빌드 접미사는 setuptools_scm 의 "+g<hash>" 다. docker 태그 문자 제한 때문에
// "-g<hash>" 로 바뀐 형태다. 접미사는 받아들이기만 하고 순위에는 쓰지 않는다. 받지 않으면 실제
// dev 태그가 하나도 2그룹에 들어가지 못하고 모두 3그룹 알파벳순으로 간다.
// 접미사 시작을 [-+] 로 제한한다. 그래서 "1.2.3-dev.1" 처럼 dev 번호가 모호한 태그는 3그룹에 남는다.
const DEV_SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)[._-]dev(\d*)(?:[-+][0-9A-Za-z.-]+)?$/i;

export function parseStrictSemver(tag: string): SemverTriple | null {
  const match = STRICT_SEMVER_RE.exec(tag);
  if (match === null) {
    return null;
  }
  return { major: Number(match[1]), minor: Number(match[2]), patch: Number(match[3]) };
}

function parseDevSemver(tag: string): DevSemver | null {
  const match = DEV_SEMVER_RE.exec(tag);
  if (match === null) {
    return null;
  }
  return {
    major: Number(match[1]),
    minor: Number(match[2]),
    patch: Number(match[3]),
    dev: match[4] === "" ? -1 : Number(match[4]),
  };
}

function compareTripleDesc(a: SemverTriple, b: SemverTriple): number {
  return b.major - a.major || b.minor - a.minor || b.patch - a.patch;
}

// locale 과 무관한 결정적 비교. 그룹 내 tie-break 와 3그룹 알파벳 정렬이 함께 쓴다.
function compareTagAsc(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

// metadata probe 대상은 엄격 semver(비-dev) 태그뿐이다. semver 오름차순으로 반환한다. api 계층의
// 태그별 metadata 캐시는 삽입 순서로 상한을 넘는 항목을 제거한다. 그래서 가장 유력한 승자(최고
// 버전)를 마지막에 요청해야 후보가 상한을 넘어도 승자의 캐시가 남는다(trainingEval.ts
// IMAGE_METADATA_MAX_ENTRIES).
export function semverProbeCandidates(
  tags: readonly TrainingScenarioImageTagView[],
): readonly string[] {
  const candidates: { tag: string; semver: SemverTriple }[] = [];
  for (const entry of tags) {
    const semver = parseStrictSemver(entry.tag);
    if (semver !== null) {
      candidates.push({ tag: entry.tag, semver });
    }
  }
  candidates.sort((a, b) => compareTripleDesc(b.semver, a.semver) || compareTagAsc(a.tag, b.tag));
  return candidates.map((entry) => entry.tag);
}

// probedOk 는 metadata probe 가 성공한 태그 집합이다. index 0 이 모달이 자동 선택하는 태그다.
export function rankImageTags(
  tags: readonly TrainingScenarioImageTagView[],
  probedOk: ReadonlySet<string>,
): RankedImageTag[] {
  const recommended: { entry: TrainingScenarioImageTagView; semver: SemverTriple }[] = [];
  const devTags: { entry: TrainingScenarioImageTagView; devSemver: DevSemver }[] = [];
  const rest: TrainingScenarioImageTagView[] = [];
  for (const entry of tags) {
    const devSemver = parseDevSemver(entry.tag);
    if (devSemver !== null) {
      devTags.push({ entry, devSemver });
      continue;
    }
    const semver = parseStrictSemver(entry.tag);
    if (semver !== null && probedOk.has(entry.tag)) {
      recommended.push({ entry, semver });
      continue;
    }
    rest.push(entry);
  }

  recommended.sort(
    (a, b) => compareTripleDesc(a.semver, b.semver) || compareTagAsc(a.entry.tag, b.entry.tag),
  );
  devTags.sort(
    (a, b) =>
      compareTripleDesc(a.devSemver, b.devSemver) ||
      b.devSemver.dev - a.devSemver.dev ||
      compareTagAsc(a.entry.tag, b.entry.tag),
  );
  rest.sort((a, b) => compareTagAsc(a.tag, b.tag));

  const toRanked = (
    entry: TrainingScenarioImageTagView,
    isRecommended: boolean,
  ): RankedImageTag => ({
    tag: entry.tag,
    imageUri: entry.image_uri,
    recommended: isRecommended,
  });
  return [
    ...recommended.map((item) => toRanked(item.entry, true)),
    ...devTags.map((item) => toRanked(item.entry, false)),
    ...rest.map((entry) => toRanked(entry, false)),
  ];
}
